export const DEFAULT_RTC_PEER_CONNECTION_CONFIG = {
  bundlePolicy: 'max-bundle',
  iceCandidatePoolSize: 8,
  iceTransportPolicy: 'all',
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
};

const PUSHER_COMPRESS_THRESHOLD = 9000;

export async function compressPusherPayload(payload) {
  const json = JSON.stringify(payload);
  if (json.length < PUSHER_COMPRESS_THRESHOLD) return payload;
  const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('deflate'));
  const compressed = await new Response(stream).arrayBuffer();
  const base64 = btoa(String.fromCharCode(...new Uint8Array(compressed)));
  console.debug(`compressed pusher payload: ${json.length} -> ${base64.length + 20} bytes`);
  return { _compressed: base64 };
}

export async function decompressPusherPayload(data) {
  if (!data._compressed) return data;
  const bytes = Uint8Array.from(atob(data._compressed), c => c.charCodeAt(0));
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate'));
  const json = await new Response(stream).text();
  console.debug(`decompressed pusher payload: ${data._compressed.length + 20} -> ${json.length} bytes`);
  return JSON.parse(json);
}

/**
 * Converts a TURN server URL to an RTCIceServer object.
 * @param {string} url
 * @returns {RTCIceServer|null}
 */
export function buildRTCIceServer(url) {
  const re = /^(turns?:)\/\/([^:]+):([^@]+)@(.+)$/
  const matches = url.match(re);
  if (!matches) {
    console.error(`invalid turn server url: ${url}`);
    return null;
  }
  return {
    username: decodeURIComponent(matches[2]),
    credential: decodeURIComponent(matches[3]),
    urls: `${matches[1]}${matches[4]}`,
  };
}

/**
 * Builds an RTCConfiguration by merging TURN servers into a default config.
 * @param {RTCConfiguration} defaultConfig
 * @param {string[]|null} turnServers - array of TURN server URLs
 * @returns {RTCConfiguration}
 */
export function buildRTCPeerConnectionConfig(defaultConfig, turnServers) {
  let connConfig = defaultConfig
  if (turnServers) {
    // copy the ice server list as well, otherwise the TURN entries are pushed into the caller's (shared) default config
    connConfig = { ...connConfig, iceServers: [...(connConfig.iceServers || [])] }
    for (const turnServer of turnServers) {
      const iceServer = buildRTCIceServer(turnServer)
      if (iceServer) {
        connConfig.iceServers.push(iceServer)
      }
    }
  }
  return connConfig
}

/**
 * Handles peer negotiation (perfect negotiation pattern with collision detection).
 * Used identically by both the Vue participant and the kiosk.
 *
 * @param {Object} params
 * @param {RTCPeerConnection} params.rtcPeerConnection
 * @param {RTCDataChannel} params.dataChannel
 * @param {string} params.localPeerId - this peer's id (used for tie-breaking and reply)
 * @param {string} params.remotePeerId - the remote peer's id (used for tie-breaking)
 * @param {boolean} params.makingOffer - whether this peer is currently making a negotiation offer
 * @param {RTCSessionDescriptionInit} params.sdp - the received SDP
 */
export async function handlePeerNegotiation({ rtcPeerConnection, dataChannel, localPeerId, remotePeerId, makingOffer, sdp }) {
  const offerCollision = (sdp.type === 'offer') &&
    (makingOffer || rtcPeerConnection.signalingState !== 'stable');

  // tie-breaker based on peer id to avoid issue with simultaneous offers
  const polite = localPeerId < remotePeerId;

  const ignoreOffer = !polite && offerCollision;
  if (ignoreOffer) {
    console.warn(`ignoring negotiation offer from ${remotePeerId} due to collision`);
    return;
  }

  await rtcPeerConnection.setRemoteDescription(sdp);
  console.debug(`set remote description from negotiation offer from ${remotePeerId}`);

  if (sdp.type === 'offer') {
    await rtcPeerConnection.setLocalDescription();
    console.debug(`created and set local description answer from ${remotePeerId}`);
    try {
      dataChannel.send(JSON.stringify({
        type: "peer-negotiation",
        sdp: rtcPeerConnection.localDescription,
        fromPeerId: localPeerId,
      }));
      console.debug(`sent peer-negotiation to ${remotePeerId}`);
    } catch (err) {
      console.error(`error sending peer-negotiation answer to ${remotePeerId}`, err);
    }
  }
}

/**
 * Sends a negotiation offer over a data channel (outbound half of perfect negotiation).
 * The caller is responsible for managing the makingOffer flag in try/finally.
 *
 * @param {Object} params
 * @param {RTCPeerConnection} params.rtcPeerConnection
 * @param {RTCDataChannel} params.dataChannel
 * @param {string} params.localPeerId - this peer's id
 * @returns {Promise<{sent: boolean}>}
 */
export async function sendNegotiationOffer({ rtcPeerConnection, dataChannel, localPeerId }) {
  if (!dataChannel || dataChannel.readyState !== 'open') {
    console.warn(`negotiation needed but data channel is not open`);
    return { sent: false };
  }
  await rtcPeerConnection.setLocalDescription();
  dataChannel.send(JSON.stringify({
    type: "peer-negotiation",
    sdp: rtcPeerConnection.localDescription,
    fromPeerId: localPeerId,
  }));
  console.debug(`sent negotiation offer from ${localPeerId}`);
  return { sent: true };
}

/**
 * Sets the remote SDP description and adds ICE candidates.
 *
 * @param {RTCPeerConnection} rtcPeerConnection
 * @param {RTCSessionDescriptionInit} remoteSdp
 * @param {RTCIceCandidateInit[]} remoteIceCandidates
 */
export async function applyRemoteAnswer(rtcPeerConnection, remoteSdp, remoteIceCandidates) {
  await rtcPeerConnection.setRemoteDescription(remoteSdp);
  console.debug("remote description set");
  if (remoteIceCandidates && Array.isArray(remoteIceCandidates)) {
    for (const cand of remoteIceCandidates) {
      if (cand) {
        await rtcPeerConnection.addIceCandidate(new RTCIceCandidate(cand));
        console.debug(`added remote ICE candidate`, cand.candidate ? cand.candidate.substring(0, 50) + "..." : "(null)");
      }
    }
  }
}

/**
 * Attaches diagnostic logging listeners to an RTCPeerConnection.
 *
 * @param {RTCPeerConnection} peerConn
 * @param {string} participantId - label used in log messages
 */
export function attachPeerConnectionDiagnosticListeners(peerConn, participantId) {
  peerConn.addEventListener('signalingstatechange', () => {
    console.debug(`${participantId} signaling state changed to ${peerConn.signalingState}`);
  });
  peerConn.addEventListener('icecandidateerror', (errEvent) => {
    console.warn(`ICE CANDIDATE ERROR: (${errEvent.errorCode}) ${errEvent.errorText}`, errEvent);
  });
  peerConn.addEventListener('iceconnectionstatechange', () => {
    console.debug(`${participantId} ice connect state changed to ${peerConn.iceConnectionState}`);
  });
  peerConn.addEventListener('icegatheringstatechange', () => {
    console.debug(`${participantId} ice gather state changed to ${peerConn.iceGatheringState}`);
  });
}

/**
 * Checks if a Pusher client event is targeted at a specific peer.
 * Event names may include `@memberId` suffix for targeted delivery.
 *
 * @param {string} eventName - the Pusher event name (e.g., "client-peer-sdp-answer@peer123")
 * @param {string} myPeerId - this peer's id
 * @returns {boolean} true if the event has no target or the target matches myPeerId
 */
export function isEventTargetedAtPeer(eventName, myPeerId) {
  if (eventName.includes('@')) {
    const targetMemberId = eventName.substring(eventName.lastIndexOf('@') + 1);
    if (targetMemberId !== myPeerId) {
      return false;
    }
  }
  return true;
}
